/**
 * API client for onboarding flow.
 * SAS-314: Onboarding flows for new users.
 */

import type {
  OnboardingStatusResponse,
  OnboardingCompleteResponse,
  ProfileFormData,
  ApiError,
} from '../features/onboarding/types';
import { FeatureDisabledError } from './_http';

const BASE = '/api/users/me';
const TIMEOUT_MS = 30_000;

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      credentials: 'include',
      signal: controller.signal,
      ...init,
    });
    clearTimeout(timer);

    if (response.status === 503) {
      const body = (await response.clone().json().catch(() => null)) as
        | { error?: string; feature?: string }
        | null;
      if (body?.error === 'feature_disabled') {
        throw new FeatureDisabledError(body.feature ?? 'unknown');
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      const err = new Error(
        String(body.message ?? body.error ?? `Request failed: ${response.status}`),
      ) as ApiError;
      err.status = response.status;
      err.code = typeof body.error === 'string' ? body.error : undefined;
      err.details = body;
      throw err;
    }

    const json = await response.json() as Record<string, unknown>;
    // Support both { data: T } and direct response shapes
    return ('data' in json ? json.data : json) as T;
  } catch (err) {
    clearTimeout(timer);
    if ((err as Error).name === 'AbortError') {
      const timeoutErr = new Error('Request timed out. Please try again.') as ApiError;
      timeoutErr.code = 'TIMEOUT';
      throw timeoutErr;
    }
    throw err;
  }
}

/** Retry with exponential backoff (1s, 2s, 4s) on timeout. */
async function requestWithRetry<T>(url: string, init?: RequestInit, maxRetries = 3): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      return await request<T>(url, init);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.code !== 'TIMEOUT' || attempt >= maxRetries - 1) throw err;
      attempt++;
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
    }
  }
}

export const onboardingApi = {
  /** GET /api/users/me/onboarding — fetch current onboarding status. */
  getStatus: () => request<OnboardingStatusResponse>(`${BASE}/onboarding`),

  /**
   * PATCH /api/users/me/profile — save profile form data.
   * Retries on timeout with exponential backoff.
   */
  updateProfile: (data: ProfileFormData) =>
    requestWithRetry<{ success: boolean; user: Record<string, unknown> }>(`${BASE}/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /**
   * PATCH /api/users/me/preferences — save selected feature interests.
   * Retries on timeout with exponential backoff.
   */
  updatePreferences: (interests: string[]) =>
    requestWithRetry<{ success: boolean }>(`${BASE}/preferences`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interests }),
    }),

  /**
   * POST /api/users/me/onboarding/complete — mark onboarding done, award badge.
   * Returns recommended learning path and earned badge.
   */
  complete: () =>
    requestWithRetry<OnboardingCompleteResponse>(`${BASE}/onboarding/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),

  /**
   * POST /api/users/me/onboarding/skip — mark onboarding as skipped.
   * @param step Optional: the step the user skipped from.
   */
  skip: (step?: number) =>
    request<{ success: boolean }>(`${BASE}/onboarding/skip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(step !== undefined ? { step } : {}),
    }),

  /** Fire-and-forget telemetry event — never throws. */
  trackEvent: (event: string, data: Record<string, unknown> = {}): void => {
    fetch('/api/telemetry/events', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, ...data }),
    }).catch(() => undefined);
  },
};
